'use client';
/**
 * MoMent — LE RÉFÉRENTIEL DU DÉPISTAGE DE LA TRISOMIE 21 (lot L1, 11/10/2026).
 * ═══════════════════════════════════════════════════════════
 *
 * Ce que l'écran montre, en quatre onglets et un aperçu :
 *  · ANALYSES — ce que le CATALOGUE dit de TRISOMIE_T1/T2 (composition, seuils
 *    de classe lus au registre des intervalles) et ce que MoMent y ajoute
 *    (profil SsdwLab, fenêtre, bornes LCC, seuil AFP). Le catalogue se corrige
 *    AU CATALOGUE : rien ici ne le réécrit ;
 *  · COMMENTAIRES TYPES — la bibliothèque, ses versions, son circuit
 *    brouillon → validé → retiré. Un texte validé finit sur un compte rendu :
 *    la validation est un acte de biologiste HABILITÉ, jamais de l'auteur ;
 *  · RÈGLES — les contrôles §6 ; les 🔒 ne s'abaissent pas ;
 *  · PARAMÉTRAGE — libellés SsdwLab, appel obligatoire, plausibilité des MoM ;
 *  · APERÇU — la conclusion assemblée sur un dossier FICTIF, brouillons compris
 *    si on le demande, pour relire avant de valider ;
 *  · MODÈLES DE CR — un compte rendu par situation (datation, bornes de la
 *    fenêtre, résultats, échographie, facteurs, jumeaux, non-réalisations),
 *    fabriqué depuis un PDF SsdwLab anonymisé, et la COUVERTURE du corpus
 *    (RQ, 21/10/2026).
 *
 * ── Ce que l'écran refuse de faire ────────────────────────────────────────
 *  · AFFIRMER « complet » : `ref01` vient du moteur, qui énumère TOUTES les
 *    combinaisons. L'écran liste les défauts, il ne les résume pas en vert ;
 *  · MONTRER UN BOUTON QUI ÉCHOUERA : « Valider » n'apparaît que si le moteur
 *    a dit que l'appelant peut valider (fonction + habilitation), et le motif
 *    du refus est écrit en toutes lettres à la place ;
 *  · ÉCRIRE UNE CONDITION EN CODE LIBRE : elle se compose par cases (classes,
 *    tube neural, signaux, MoM), la grammaire est celle du moteur.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { fetchEcriture } from '@/lib/ecrire';
import { useGed, userName } from '@/lib/store';
import { fmtDateTime } from '@/lib/meta';
import { I, card, inputStyle, EmptyState, FieldError, useBusy, Selecteur, ConfirmButton, BoutonCreer, enChargement, Skeleton, Modale, Pastille } from '@/components/ui';
import { lbl, pill } from './ressources/commun';
import { EnteteEcran } from '@/components/EnteteEcran';
import { Bouton } from '@/shared/Bouton';
import { PiedActions } from '@/shared/PiedActions';
import { Segment } from '@/shared/Segment';
import { Tableau, ligneOuvrable } from '@/shared/Tableau';

/* ───────────────────────────── Types servis par le moteur ───────────────── */

type Classe = 'faible' | 'intermediaire' | 'accru';
type Condition = {
  classeT21?: Classe[]; classeT18?: Classe[]; classeMax?: Classe[]; classesDifferentes?: boolean;
  tubeNeural?: string[]; nonRealisation?: string[];
  mom?: { parametre: string; operateur: 'inferieur' | 'superieurOuEgal'; seuil: number }[];
  signaux?: string[]; sansSignaux?: string[];
};
type Commentaire = {
  documentId: string; code: string; bloc: string; analyses: string[]; texte: string; condition: Condition | null;
  mode: 'auto' | 'propose' | 'manuel'; ordre: number; version: number; statut: 'brouillon' | 'valide' | 'retire';
  auteur: string | null; auteurLe: string | null; validateur: string | null; valideLe: string | null;
  retireLe: string | null; retirePar: string | null; retireMotif: string | null; origine: string | null;
  historique: { version: number; texte: string; statut: string; auteur: string | null; validateur: string | null; figeLe: string }[];
  note: string | null;
};
type Regle = {
  code: string; libelle: string; famille: string; analyses: string[]; niveau: 'A' | 'B'; verrou: boolean; actif: boolean;
  message: string; parametres: Record<string, Record<string, unknown>> | null; commentaireNr: string | null; version: number; note: string | null;
};
type Analyse = {
  ficheCode: string; trimestre: 'T1' | 'T2'; profilSsdwLab: string; fenetreMinJours: number; fenetreMaxJours: number;
  lccMinMm: number | null; lccMaxMm: number | null; seuilAfpTubeNeuralMom: number | null; tubeNeuralDesSa: number | null;
  titreCr: string | null; titreCrSansCn: string | null; version: number; versionLe: string | null; note: string | null;
  habilitationsValidation?: { technique?: string[][] | null; biologique?: string[][] | null } | null;
  catalogue: {
    fiche: { code: string; libelle: string; famille: string; version: string } | null;
    parametres: { code: string; nom: string; unite: string | null; origine: string; type: string | null }[];
    seuils: Record<string, { seuils: { accru: number; intermediaire: number } | null; motif: string | null; lignes: number }>;
    manques: string[];
  };
  completude: { combinaisons: number; defauts: { contexte: string; defauts: string[] }[]; manquesCatalogue: string[] };
  ref01: boolean;
};
type Parametrage = {
  libellesSsdwLab: { libelle: string; facteur: string }[]; appelObligatoireAccru: boolean;
  momPlausibleMin: number; momPlausibleMax: number; commentaireLibreMax: number; habilitationValidation: string | null;
  modifieLe?: string | null; modifiePar?: string | null;
};
type SeuilRegle = { cle: string; libelle: string; unite: string; portee: string; defaut: number; min: number; max: number; aide: string };
type Referentiel = {
  analyses: Analyse[]; commentaires: Commentaire[]; regles: Regle[]; parametrage: Parametrage;
  seuilsRegles: { seuils: Record<string, SeuilRegle[]>; renvois: Record<string, string> };
  habilitations: { code: string; libelle: string }[];
  vocabulaire: { blocs: string[]; signaux: readonly string[]; facteurs: readonly string[]; variables: readonly string[] };
  droits: { peutParametrer: boolean; peutRediger: boolean; peutValider: boolean; motifValidation: string; parAnalyse?: Record<string, { ok: boolean; motif: string }> };
};
type Apercu = {
  blocs: { bloc: string; code: string; version: number; texte: string }[];
  proposes: { code: string; texte: string }[]; manuels: { code: string; texte: string }[];
  defauts: string[]; indecidables: string[]; brouillonsCompris: boolean;
};

/* ───────────────────────────── Vocabulaire d'affichage ──────────────────── */

const CLASSES: Classe[] = ['faible', 'intermediaire', 'accru'];
const LIB_CLASSE: Record<Classe, string> = { faible: 'faible', intermediaire: 'intermédiaire', accru: 'accru' };
const TUBES = ['faible', 'accru', 'absent'];
const LIB_BLOC: Record<string, string> = { LIB: 'Libellé', RES: 'Phrase résultat', TN: 'Tube neural', REC: 'Recommandation', NR: 'Non-réalisation', COM: 'Commentaire', MENTION: 'Mention fixe' };
const LIB_MODE: Record<string, string> = { auto: 'Automatique', propose: 'Proposé', manuel: 'Manuel' };
const LIB_SIGNAL: Record<string, string> = {
  ecartDatation: 'écart de datation DDR / LCC > 7 j', facteursNonPrecises: 'tous les facteurs « non précisé »',
  sansCn: 'PDF sans clarté nucale', gemellaire: 'grossesse gémellaire', monochoriale: 'jumeaux monochoriaux', chorionicitePasEvaluee: 'chorionicité non évaluée', appelTrace: 'prescripteur prévenu (appel tracé)',
  rectificatif: 'compte rendu rectificatif', echoNormaleSignalee: 'échographie normale (les dix signes évalués « Non »)',
};
const LIB_FACTEUR: Record<string, string> = {
  tabac: 'Tabac', diabeteId: 'Diabète insulino-dépendant', fiv: 'FIV / ICSI', atcdT21: 'ATCD trisomie 21',
  atcdT1318: 'ATCD trisomie 13/18', atcdMtn: 'ATCD défaut du tube neural', origineNoire: 'Origine noire', origineAsiatique: 'Origine asiatique',
};
const LIB_FAMILLE_REGLE: Record<string, string> = { identite: 'Identité', validite: 'Validité', datation: 'Datation', coherence: 'Cohérence', referentiel: 'Référentiel', 'garde-fou': 'Garde-fou du commentaire libre' };
const TEINTE_STATUT: Record<string, { bg: string; fg: string }> = {
  brouillon: { bg: 'var(--amber-50)', fg: 'var(--amber-700)' },
  valide: { bg: 'var(--green-50)', fg: 'var(--green-700)' },
  retire: { bg: 'var(--gray-50)', fg: 'var(--text-muted)' },
};
const LIB_STATUT: Record<string, string> = { brouillon: 'Brouillon', valide: 'Validé', retire: 'Retiré' };
const encart: React.CSSProperties = {
  background: 'var(--amber-50)', borderInlineStart: '3px solid var(--amber-500)',
  borderRadius: 'var(--radius-sm)', padding: '9px 13px', fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5,
};
const encartVert: React.CSSProperties = { ...encart, background: 'var(--green-50)', borderInlineStartColor: 'var(--green-700)' };
const mono: React.CSSProperties = { fontFamily: 'var(--font-mono)', fontSize: 11.5 };
/* Dans un tableau, la taille vient du socle (`--tableau-ligne-police`) : rien en dur. */
const monoCellule: React.CSSProperties = { fontFamily: 'var(--font-mono)' };
const texteStyle: React.CSSProperties = { ...inputStyle, height: 'auto', padding: '9px 12px', resize: 'vertical' };

/** 77 → « 11 SA + 0 j » : les jours se lisent, ils ne se comptent pas. */
const sa = (j: number) => `${Math.floor(j / 7)} SA + ${j % 7} j`;
/** 0,004 → « 1/250 » : un seuil de classe se lit en fraction. */
const fraction = (p: number) => `1/${Math.round(1 / p).toLocaleString('fr-FR')}`;

async function ecrire(chemin: string, corps: unknown): Promise<{ ok: boolean; message: string; corps: Record<string, unknown> | null }> {
  const r = await fetchEcriture(chemin, {
    method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(corps),
  });
  const j = await r.json().catch(() => null);
  return { ok: r.ok, message: j?.error?.message || '', corps: j };
}
const URL_REF = '/api/ged/ged/moment/referentiel';

/* ───────────────────────────── Condition : lecture et saisie ─────────────── */

function phraseCondition(c: Condition | null): string {
  if (!c || !Object.keys(c).length) return 'toujours';
  const parts: string[] = [];
  const classes = (l?: Classe[]) => (l || []).map((x) => LIB_CLASSE[x]).join(' ou ');
  if (c.classeMax) parts.push(`classe la plus haute ${classes(c.classeMax)}`);
  if (c.classeT21) parts.push(`T21 ${classes(c.classeT21)}`);
  if (c.classeT18) parts.push(`T13/18 ${classes(c.classeT18)}`);
  if (c.classesDifferentes !== undefined) parts.push(c.classesDifferentes ? 'classes différentes' : 'classes identiques');
  if (c.tubeNeural) parts.push(`tube neural ${c.tubeNeural.join(' ou ')}`);
  if (c.nonRealisation) parts.push(`non-réalisation ${c.nonRealisation.join(', ')}`);
  for (const m of c.mom || []) parts.push(`${m.parametre} ${m.operateur === 'inferieur' ? '<' : '≥'} ${String(m.seuil).replace('.', ',')} MoM`);
  for (const s of c.signaux || []) parts.push(LIB_SIGNAL[s] || s);
  for (const s of c.sansSignaux || []) parts.push(`sans ${LIB_SIGNAL[s] || s}`);
  return parts.join(' · ');
}

function Cases<T extends string>({ titre, options, libelle, choisis, onChange }: {
  titre: string; options: readonly T[]; libelle: (v: T) => string; choisis: T[]; onChange: (v: T[]) => void;
}) {
  return (
    <div>
      <div style={lbl}>{titre}</div>
      <div style={{ display: 'flex', gap: '6px 14px', flexWrap: 'wrap', fontSize: 12.5 }}>
        {options.map((o) => (
          <label key={o} style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
            <input type="checkbox" checked={choisis.includes(o)}
              onChange={(e) => onChange(e.target.checked ? [...choisis, o] : choisis.filter((x) => x !== o))} />
            {libelle(o)}
          </label>
        ))}
      </div>
    </div>
  );
}

function ConditionForm({ c, onChange, signaux, parametres }: {
  c: Condition; onChange: (c: Condition) => void; signaux: readonly string[]; parametres: string[];
}) {
  const poser = (k: keyof Condition, v: unknown) => {
    const n: Condition = { ...c };
    if (v === undefined || (Array.isArray(v) && !v.length)) delete n[k]; else (n as Record<string, unknown>)[k] = v;
    onChange(n);
  };
  return (
    <div style={{ display: 'grid', gap: 10, padding: 12, border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)' }}>
      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Quand le texte s’applique — tout ce qui est coché doit tenir. Rien de coché : toujours.</div>
      <Cases titre="Classe la plus haute (T21 ou T13/18)" options={CLASSES} libelle={(v) => LIB_CLASSE[v]} choisis={c.classeMax || []} onChange={(v) => poser('classeMax', v)} />
      <Cases titre="Classe T21" options={CLASSES} libelle={(v) => LIB_CLASSE[v]} choisis={c.classeT21 || []} onChange={(v) => poser('classeT21', v)} />
      <Cases titre="Classe T13/18" options={CLASSES} libelle={(v) => LIB_CLASSE[v]} choisis={c.classeT18 || []} onChange={(v) => poser('classeT18', v)} />
      <div>
        <div style={lbl}>Classes T21 et T13/18</div>
        <Selecteur id="cond-diff" value={c.classesDifferentes === undefined ? '' : c.classesDifferentes ? 'oui' : 'non'}
          onChange={(v) => poser('classesDifferentes', v === '' ? undefined : v === 'oui')} style={{ ...inputStyle, maxWidth: 260 }}>
          <option value="">— indifférent —</option><option value="oui">différentes</option><option value="non">identiques</option>
        </Selecteur>
      </div>
      <Cases titre="Tube neural (2e trimestre)" options={TUBES} libelle={(v) => v} choisis={c.tubeNeural || []} onChange={(v) => poser('tubeNeural', v)} />
      <div><label style={lbl} htmlFor="cond-nr">Codes de non-réalisation (NR-…, séparés par des virgules)</label>
        <input id="cond-nr" value={(c.nonRealisation || []).join(', ')} style={inputStyle}
          onChange={(e) => poser('nonRealisation', e.target.value.split(',').map((x) => x.trim().toUpperCase()).filter(Boolean))} /></div>
      <div>
        <div style={lbl}>MoM d’un paramètre</div>
        {(c.mom || []).map((m, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 120px 100px auto', gap: 8, marginBottom: 6 }}>
            <Selecteur aria-label="Paramètre" value={m.parametre} onChange={(v) => poser('mom', (c.mom || []).map((x, k) => (k === i ? { ...x, parametre: v } : x)))} style={inputStyle}>
              {parametres.map((p) => <option key={p} value={p}>{p}</option>)}
            </Selecteur>
            <Selecteur aria-label="Opérateur" value={m.operateur} onChange={(v) => poser('mom', (c.mom || []).map((x, k) => (k === i ? { ...x, operateur: v as 'inferieur' | 'superieurOuEgal' } : x)))} style={inputStyle}>
              <option value="inferieur">&lt;</option><option value="superieurOuEgal">≥</option>
            </Selecteur>
            <input aria-label="Seuil (MoM)" type="number" step="0.01" min={0} value={m.seuil} style={inputStyle}
              onChange={(e) => poser('mom', (c.mom || []).map((x, k) => (k === i ? { ...x, seuil: Number(e.target.value) } : x)))} />
            <Bouton taille="sm" variante="discret" icone="x" aria-label="Retirer cette MoM" onClick={() => poser('mom', (c.mom || []).filter((_, k) => k !== i))} />
          </div>
        ))}
        <Bouton taille="sm" onClick={() => poser('mom', [...(c.mom || []), { parametre: parametres[0] || 'PAPP_A', operateur: 'inferieur', seuil: 0.4 }])}><I n="plus" s={12} />Ajouter une MoM</Bouton>
      </div>
      <Cases titre="Signaux du dossier (tous présents)" options={signaux} libelle={(v) => LIB_SIGNAL[v] || v} choisis={c.signaux || []} onChange={(v) => poser('signaux', v)} />
      <Cases titre="Signaux absents" options={signaux} libelle={(v) => LIB_SIGNAL[v] || v} choisis={c.sansSignaux || []} onChange={(v) => poser('sansSignaux', v)} />
    </div>
  );
}

/* ───────────────────────────── Onglet Analyses ─────────────────────────── */

/* ───────────────── Habilitations exigées pour valider un examen ─────────────── */

type Exigence = string[][];
/** « (A ET B) OU C » — la même lecture que le moteur (`phraseExigence`). */
const phraseExigence = (e: Exigence) => (e.length
  ? e.map((g) => (e.length > 1 && g.length > 1 ? `(${g.join(' ET ')})` : g.join(' ET '))).join(' OU ')
  : '');

/**
 * Qui peut valider cet examen, techniquement et biologiquement (RQ, 13/10/2026).
 * Une exigence est une liste d'ALTERNATIVES (OU) ; chacune est un groupe d'habilitations
 * TOUTES requises (ET). Vide = rien d'exigé ; la validation biologique retombe alors sur
 * l'habilitation unique du paramétrage MoMent, et l'écran le dit.
 */
function ExigencesHabilitation({ a, habilitations, parDefaut, peutParametrer, onFait }: {
  a: Analyse; habilitations: { code: string; libelle: string }[]; parDefaut: string | null; peutParametrer: boolean; onFait: () => void;
}) {
  const { showToast } = useGed();
  const [edition, setEdition] = useState(false);
  const depart = (): Record<'technique' | 'biologique', Exigence> => ({
    technique: (a.habilitationsValidation?.technique || []).map((g) => [...g]),
    biologique: (a.habilitationsValidation?.biologique || []).map((g) => [...g]),
  });
  const [f, setF] = useState(depart);
  const [erreur, setErreur] = useState('');
  const { run, estOccupe } = useBusy();
  const libelle = (code: string) => habilitations.find((h) => h.code === code)?.libelle || code;
  const enregistrer = () => run('save-hab', async () => {
    setErreur('');
    const r = await ecrire(`${URL_REF}/analyses/${encodeURIComponent(a.ficheCode)}`, { habilitationsValidation: f });
    if (!r.ok) { setErreur(r.message || 'Les exigences n’ont pas pu être enregistrées.'); return; }
    showToast(`Exigences de validation de ${a.ficheCode} enregistrées.`);
    setEdition(false); onFait();
  });
  const bloc = (quoi: 'technique' | 'biologique', titre: string, aide: string) => {
    const lecture = a.habilitationsValidation?.[quoi] || [];
    return (
      <div style={{ display: 'grid', gap: 6 }}>
        <div style={lbl}>{titre}</div>
        {!edition ? (
          <div style={{ fontSize: 12.5 }}>
            {lecture.length
              ? <strong>{phraseExigence(lecture)}</strong>
              : <span style={{ color: 'var(--text-muted)' }}>
                {quoi === 'biologique'
                  ? <>Aucune exigence propre à cet examen : l’habilitation du paramétrage MoMento s’applique ({parDefaut ? <span style={mono}>{parDefaut}</span> : 'aucune — la fonction biologiste suffit'}).</>
                  : 'Aucune exigence : personne n’est bloqué pour cet acte.'}
              </span>}
            <div style={{ color: 'var(--text-muted)', marginTop: 2 }}>{aide}</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 8 }}>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{aide}</div>
            {f[quoi].map((g, i) => (
              <div key={i} style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', padding: 10, display: 'grid', gap: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <strong style={{ fontSize: 12.5 }}>{i === 0 ? 'Il faut' : 'OU bien'} — toutes les habilitations cochées (ET)</strong>
                  <span style={{ flex: 1 }} />
                  <Bouton taille="sm" variante="discret" icone="x" aria-label={`Retirer l’alternative ${i + 1}`} onClick={() => setF({ ...f, [quoi]: f[quoi].filter((_, j) => j !== i) })} />
                </div>
                <Cases titre="" options={habilitations.map((h) => h.code)} libelle={(c) => `${c} — ${libelle(c)}`} choisis={g}
                  onChange={(v) => setF({ ...f, [quoi]: f[quoi].map((x, j) => (j === i ? v : x)) })} />
              </div>
            ))}
            <div><Bouton taille="sm" onClick={() => setF({ ...f, [quoi]: [...f[quoi], []] })}><I n="plus" s={11} />{f[quoi].length ? 'Ajouter une alternative (OU)' : 'Exiger des habilitations'}</Bouton></div>
          </div>
        )}
      </div>
    );
  };
  return (
    <div style={{ borderTop: '1px solid var(--border-default)', paddingTop: 12, display: 'grid', gap: 10 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 13, color: 'var(--text-strong)' }}>Habilitations exigées pour valider cet examen</strong>
        <span style={{ flex: 1 }} />
        {peutParametrer && !edition && <Bouton taille="sm" onClick={() => { setF(depart()); setEdition(true); }}><I n="pen" s={12} />Exigences de validation</Bouton>}
      </div>
      <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 20px' }}>
        {bloc('technique', 'Validation technique', 'Appliquée à la levée d’un avertissement d’un dossier (l’acte technique de MoMento).')}
        {bloc('biologique', 'Validation biologique', 'Textes des comptes rendus, validation, dévalidation et rectification d’un CR, modification d’un risque. La fonction biologiste est toujours exigée.')}
      </div>
      {edition && (<>
        <FieldError>{erreur}</FieldError>
        {!habilitations.length && <div style={encart}>Aucune habilitation active n’a pu être lue au référentiel RH : il n’y a rien à exiger.</div>}
        <PiedActions taille="sm" annuler={{ onClick: () => setEdition(false) }}
          principale={{ libelle: 'Enregistrer les exigences', libelleChargement: 'Enregistrement…', chargement: estOccupe('save-hab'), disabled: estOccupe() || f.technique.concat(f.biologique).some((g) => !g.length), onClick: () => void enregistrer() }} />
      </>)}
    </div>
  );
}

function AnalyseCarte({ a, peutParametrer, habilitations, parDefaut, onFait }: { a: Analyse; peutParametrer: boolean; habilitations: { code: string; libelle: string }[]; parDefaut: string | null; onFait: () => void }) {
  const { showToast } = useGed();
  const [edition, setEdition] = useState(false);
  const [defautsOuverts, setDefautsOuverts] = useState(false);
  const formDe = () => ({
    profilSsdwLab: a.profilSsdwLab, fenetreMinJours: String(a.fenetreMinJours), fenetreMaxJours: String(a.fenetreMaxJours),
    lccMinMm: a.lccMinMm === null ? '' : String(a.lccMinMm), lccMaxMm: a.lccMaxMm === null ? '' : String(a.lccMaxMm),
    seuilAfpTubeNeuralMom: a.seuilAfpTubeNeuralMom === null ? '' : String(a.seuilAfpTubeNeuralMom),
    tubeNeuralDesSa: a.tubeNeuralDesSa === null ? '' : String(a.tubeNeuralDesSa),
    titreCr: a.titreCr || '', titreCrSansCn: a.titreCrSansCn || '', note: a.note || '',
  });
  const n = (v: string) => (v.trim() === '' ? null : Number(v.replace(',', '.')));
  const corpsDe = (x: ReturnType<typeof formDe>) => ({
    profilSsdwLab: x.profilSsdwLab, fenetreMinJours: n(x.fenetreMinJours), fenetreMaxJours: n(x.fenetreMaxJours),
    lccMinMm: n(x.lccMinMm), lccMaxMm: n(x.lccMaxMm), seuilAfpTubeNeuralMom: n(x.seuilAfpTubeNeuralMom), tubeNeuralDesSa: n(x.tubeNeuralDesSa),
    titreCr: x.titreCr, titreCrSansCn: x.titreCrSansCn, note: x.note,
  });
  const [f, setF] = useState(formDe);
  /* CE QUE L'ÉCRAN A LU, figé à l'ouverture (14/10/2026) : le serveur n'écrit que ce qui en diffère,
     et refuse (409) un champ qu'un autre a changé entre-temps. */
  const [base] = useState(() => corpsDe(formDe()));
  const [erreur, setErreur] = useState('');
  const { run, estOccupe } = useBusy();
  const enregistrer = () => run('save', async () => {
    setErreur('');
    const r = await ecrire(`${URL_REF}/analyses/${encodeURIComponent(a.ficheCode)}`, { ...corpsDe(f), base });
    if (!r.ok) { setErreur(r.message || 'Les paramètres n’ont pas pu être enregistrés.'); return; }
    showToast(`Paramètres de ${a.ficheCode} enregistrés — version ${String(r.corps?.version ?? a.version)}.`);
    setEdition(false); onFait();
  });
  const cat = a.catalogue;
  return (
    <div style={{ ...card, padding: 16, display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 14.5, color: 'var(--text-strong)' }}>{cat.fiche?.libelle || a.ficheCode}</strong>
        <span style={{ ...mono, color: 'var(--text-muted)' }}>{a.ficheCode}{cat.fiche ? ` · catalogue v${cat.fiche.version}` : ''} · MoMento v{a.version}</span>
        {a.ref01
          ? pill({ label: 'REF-01 : référentiel complet', bg: 'var(--green-50)', fg: 'var(--green-700)' })
          : pill({ label: `REF-01 : incomplet — ${a.completude.defauts.length + a.completude.manquesCatalogue.length} défaut(s)`, bg: 'var(--red-50)', fg: 'var(--red-600)' })}
        <span style={{ flex: 1 }} />
        {peutParametrer && !edition && <Bouton taille="sm" onClick={() => setEdition(true)}><I n="pen" s={12} />Paramètres MoMento</Bouton>}
      </div>

      <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 20px', fontSize: 12.5 }}>
        <div style={{ display: 'grid', gap: 6 }}>
          <div style={lbl}>Ce que le catalogue dit</div>
          {!cat.fiche && <div style={{ color: 'var(--red-600)' }}>Examen introuvable au catalogue publié.</div>}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {cat.parametres.map((p) => pill({
              label: `${p.code}${p.unite ? ` · ${p.unite}` : ''}${p.origine === 'calcule' ? ' · calculé' : ''}`,
              bg: p.origine === 'calcule' ? 'var(--cyan-50)' : 'var(--gray-50)', fg: 'var(--text-body)',
            }))}
          </div>
          {Object.entries(cat.seuils).map(([code, s]) => (
            <div key={code}>
              <span style={mono}>{code}</span> — {s.seuils
                ? <>seuils de classe : intermédiaire dès <strong>{fraction(s.seuils.intermediaire)}</strong>, accru dès <strong>{fraction(s.seuils.accru)}</strong> ({s.lignes} ligne(s) au registre)</>
                : <span style={{ color: 'var(--red-600)' }}>{s.motif}</span>}
            </div>
          ))}
          {cat.manques.map((m) => <div key={m} style={{ color: 'var(--red-600)' }}>⚠ {m}</div>)}
          <div style={{ color: 'var(--text-muted)' }}>Composition, unités et seuils se corrigent au référentiel des analyses — jamais ici.</div>
        </div>
        <div style={{ display: 'grid', gap: 6 }}>
          <div style={lbl}>Ce que MoMento ajoute</div>
          {!edition ? (<>
            <div>Profil SsdwLab : <strong>{a.profilSsdwLab}</strong> · {a.trimestre}</div>
            <div>Fenêtre de prélèvement : <strong>{sa(a.fenetreMinJours)}</strong> → <strong>{sa(a.fenetreMaxJours)}</strong></div>
            <div>LCC : {a.lccMinMm !== null && a.lccMaxMm !== null ? <strong>{a.lccMinMm} → {a.lccMaxMm} mm</strong> : <em>pas de contrôle</em>}</div>
            {a.trimestre === 'T2' && <div>Tube neural : AFP ≥ <strong>{a.seuilAfpTubeNeuralMom ?? '—'} MoM</strong> dès <strong>{a.tubeNeuralDesSa ?? '—'} SA</strong> (lu du PDF, jamais calculé)</div>}
            <div>Titre du CR : {a.titreCr || '—'}{a.titreCrSansCn ? <> · sans CN : {a.titreCrSansCn}</> : null}</div>
            {a.note && <div style={{ color: 'var(--text-muted)' }}>{a.note}</div>}
          </>) : (
            <div style={{ display: 'grid', gap: 8 }}>
              <div><label style={lbl} htmlFor={`an-profil-${a.ficheCode}`}>Profil SsdwLab (libellé exact du PDF)</label>
                <input id={`an-profil-${a.ficheCode}`} value={f.profilSsdwLab} onChange={(e) => setF({ ...f, profilSsdwLab: e.target.value })} style={inputStyle} /></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div><label style={lbl} htmlFor={`an-fmin-${a.ficheCode}`}>Fenêtre : début (jours, 77 = 11 SA + 0)</label>
                  <input id={`an-fmin-${a.ficheCode}`} type="number" value={f.fenetreMinJours} onChange={(e) => setF({ ...f, fenetreMinJours: e.target.value })} style={inputStyle} /></div>
                <div><label style={lbl} htmlFor={`an-fmax-${a.ficheCode}`}>Fin incluse (97 = 13 SA + 6)</label>
                  <input id={`an-fmax-${a.ficheCode}`} type="number" value={f.fenetreMaxJours} onChange={(e) => setF({ ...f, fenetreMaxJours: e.target.value })} style={inputStyle} /></div>
                <div><label style={lbl} htmlFor={`an-lmin-${a.ficheCode}`}>LCC min (mm)</label>
                  <input id={`an-lmin-${a.ficheCode}`} type="number" step="0.1" value={f.lccMinMm} onChange={(e) => setF({ ...f, lccMinMm: e.target.value })} style={inputStyle} /></div>
                <div><label style={lbl} htmlFor={`an-lmax-${a.ficheCode}`}>LCC max (mm)</label>
                  <input id={`an-lmax-${a.ficheCode}`} type="number" step="0.1" value={f.lccMaxMm} onChange={(e) => setF({ ...f, lccMaxMm: e.target.value })} style={inputStyle} /></div>
                {a.trimestre === 'T2' && (<>
                  <div><label style={lbl} htmlFor={`an-afp-${a.ficheCode}`}>Seuil AFP tube neural (MoM)</label>
                    <input id={`an-afp-${a.ficheCode}`} type="number" step="0.1" value={f.seuilAfpTubeNeuralMom} onChange={(e) => setF({ ...f, seuilAfpTubeNeuralMom: e.target.value })} style={inputStyle} /></div>
                  <div><label style={lbl} htmlFor={`an-tn-${a.ficheCode}`}>Calculé dès (SA)</label>
                    <input id={`an-tn-${a.ficheCode}`} type="number" value={f.tubeNeuralDesSa} onChange={(e) => setF({ ...f, tubeNeuralDesSa: e.target.value })} style={inputStyle} /></div>
                </>)}
              </div>
              <div><label style={lbl} htmlFor={`an-titre-${a.ficheCode}`}>Titre du CR</label>
                <input id={`an-titre-${a.ficheCode}`} value={f.titreCr} onChange={(e) => setF({ ...f, titreCr: e.target.value })} style={inputStyle} /></div>
              {a.trimestre === 'T2' && <div><label style={lbl} htmlFor={`an-titrecn-${a.ficheCode}`}>Titre de la variante sans clarté nucale</label>
                <input id={`an-titrecn-${a.ficheCode}`} value={f.titreCrSansCn} onChange={(e) => setF({ ...f, titreCrSansCn: e.target.value })} style={inputStyle} /></div>}
              <div><label style={lbl} htmlFor={`an-note-${a.ficheCode}`}>Note</label>
                <textarea id={`an-note-${a.ficheCode}`} rows={2} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} style={texteStyle} /></div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Profil, fenêtre, LCC et seuil AFP sont définissants : les changer monte la version. Les titres et la note, non.</div>
              <FieldError>{erreur}</FieldError>
              <PiedActions taille="sm" annuler={{ onClick: () => setEdition(false) }}
                principale={{ libelle: 'Enregistrer', libelleChargement: 'Enregistrement…', chargement: estOccupe('save'), disabled: estOccupe(), onClick: () => void enregistrer() }} />
            </div>
          )}
        </div>
      </div>

      <ExigencesHabilitation a={a} habilitations={habilitations} parDefaut={parDefaut} peutParametrer={peutParametrer} onFait={onFait} />

      {!a.ref01 && (
        <div style={encart}>
          <strong style={{ color: 'var(--text-strong)' }}>REF-01 bloque la validation de tout CR de cette analyse.</strong>{' '}
          {a.completude.combinaisons} combinaisons éprouvées (classes T21 × T13/18{a.trimestre === 'T2' ? ' × tube neural' : ''} + non-réalisations) ;{' '}
          {a.completude.defauts.length} en défaut{a.completude.manquesCatalogue.length ? `, ${a.completude.manquesCatalogue.length} manque(s) de catalogue` : ''}.
          {' '}Seuls les commentaires <strong>validés</strong> comptent : tant que la bibliothèque est en brouillon, tout est en défaut, et c’est exact.
          {a.completude.defauts.length > 0 && (
            <div style={{ marginTop: 6 }}>
              <Bouton taille="sm" variante="discret" onClick={() => setDefautsOuverts(!defautsOuverts)}>{defautsOuverts ? 'Replier le détail' : 'Voir le détail'}</Bouton>
              {defautsOuverts && (
                <ul style={{ margin: '6px 0 0', paddingInlineStart: 18 }}>
                  {a.completude.defauts.map((d) => <li key={d.contexte}><strong>{d.contexte}</strong> : {d.defauts.join(' ; ')}</li>)}
                </ul>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ───────────────────────────── Onglet Commentaires ─────────────────────── */

type FormCommentaire = { code: string; bloc: string; analyses: string[]; texte: string; condition: Condition; mode: string; ordre: string; origine: string; note: string };

function CommentaireForm({ c, referentiel, onFait, onAnnule }: { c: Commentaire | null; referentiel: Referentiel; onFait: () => void; onAnnule: () => void }) {
  const { showToast } = useGed();
  const formDe = (): FormCommentaire => ({
    code: c?.code || '', bloc: c?.bloc || 'COM', analyses: c?.analyses || referentiel.analyses.map((a) => a.ficheCode),
    texte: c?.texte || '', condition: c?.condition || {}, mode: c?.mode || 'auto', ordre: String(c?.ordre ?? 100), origine: c?.origine || '', note: c?.note || '',
  });
  const corpsDe = (x: FormCommentaire) => ({ ...x, ordre: Number(x.ordre), condition: Object.keys(x.condition).length ? x.condition : null });
  const [f, setF] = useState<FormCommentaire>(formDe);
  /* Le texte LU à l'ouverture (14/10/2026) : un formulaire resté ouvert ne refait plus une nouvelle
     version avec l'ancien texte — le serveur n'écrit que ce qui a changé, et refuse un conflit. */
  const [base] = useState(() => corpsDe(formDe()));
  const [erreur, setErreur] = useState('');
  const { run, estOccupe } = useBusy();
  const parametres = useMemo(() => [...new Set(referentiel.analyses.flatMap((a) => a.catalogue.parametres.filter((p) => p.origine !== 'calcule').map((p) => p.code)))], [referentiel]);
  const enregistrer = () => run('save', async () => {
    setErreur('');
    const corps = corpsDe(f);
    const r = c
      ? await ecrire(`${URL_REF}/commentaires/${encodeURIComponent(c.code)}`, { ...corps, base })
      : await ecrire(`${URL_REF}/commentaires`, { ...corps, code: `${f.bloc}-${f.code.replace(/^[A-Z]+-/, '')}` });
    if (!r.ok) { setErreur(r.message || 'Le commentaire n’a pas pu être enregistré.'); return; }
    showToast(c ? (r.corps?.nouvelleVersion ? `${c.code} : nouvelle version v${String(r.corps.version)}, en brouillon.` : `${c.code} : enregistré, version inchangée.`) : 'Commentaire créé, en brouillon.');
    onFait();
  });
  return (
    <Modale titre={c ? `Modifier ${c.code} (v${c.version})` : 'Nouveau commentaire type'} onClose={onAnnule} largeur={760}
      pied={<PiedActions taille="sm" annuler={{ onClick: onAnnule }}
        principale={{ libelle: c ? 'Enregistrer' : 'Créer en brouillon', libelleChargement: 'Enregistrement…', chargement: estOccupe('save'), disabled: estOccupe() || !f.analyses.length || (!c && !f.code), onClick: () => void enregistrer() }} />}>
    <div style={{ display: 'grid', gap: 12 }}>
      {c && c.statut === 'valide' && <div style={encart}>Modifier le texte, la condition, le mode ou les analyses crée une <strong>nouvelle version</strong>, qui repasse en brouillon. La v{c.version} reste figée dans l’historique, et les CR qui l’ont citée la gardent.</div>}
      <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px' }}>
        <div><label style={lbl} htmlFor="cm-bloc">Bloc</label>
          <Selecteur id="cm-bloc" value={f.bloc} onChange={(v) => setF({ ...f, bloc: v })} style={inputStyle} disabled={!!c}>
            {referentiel.vocabulaire.blocs.map((b) => <option key={b} value={b}>{b} — {LIB_BLOC[b] || b}</option>)}
          </Selecteur></div>
        <div><label style={lbl} htmlFor="cm-code">Code{c ? '' : ' (suffixe : le bloc est ajouté devant)'}</label>
          <input id="cm-code" value={c ? c.code : f.code} disabled={!!c} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} style={{ ...inputStyle, ...mono }} placeholder="PAPP, DAT, 08…" /></div>
        <div><label style={lbl} htmlFor="cm-mode">Mode</label>
          <Selecteur id="cm-mode" value={f.mode} onChange={(v) => setF({ ...f, mode: v })} style={inputStyle}>
            <option value="auto">Automatique — inséré sans clic</option><option value="propose">Proposé — coché par le biologiste</option><option value="manuel">Manuel — bibliothèque</option>
          </Selecteur></div>
        <div><label style={lbl} htmlFor="cm-ordre">Ordre dans son bloc</label>
          <input id="cm-ordre" type="number" value={f.ordre} onChange={(e) => setF({ ...f, ordre: e.target.value })} style={inputStyle} /></div>
        <div style={{ gridColumn: '1 / -1' }}>
          <Cases titre="Analyses" options={referentiel.analyses.map((a) => a.ficheCode)} libelle={(v) => v} choisis={f.analyses} onChange={(v) => setF({ ...f, analyses: v })} />
        </div>
        <div style={{ gridColumn: '1 / -1' }}><label style={lbl} htmlFor="cm-texte">Texte imprimé — variables : {referentiel.vocabulaire.variables.map((v) => `{${v}}`).join(' ')}</label>
          <textarea id="cm-texte" rows={4} value={f.texte} onChange={(e) => setF({ ...f, texte: e.target.value })} style={texteStyle} />
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>Un texte vide est permis et veut dire « rien à imprimer » (REC-F : pas de recommandation pour un risque faible).</div></div>
        <div style={{ gridColumn: '1 / -1' }}>
          <ConditionForm c={f.condition} onChange={(cond) => setF({ ...f, condition: cond })} signaux={referentiel.vocabulaire.signaux} parametres={parametres} />
        </div>
        <div><label style={lbl} htmlFor="cm-origine">Origine du texte</label>
          <input id="cm-origine" value={f.origine} onChange={(e) => setF({ ...f, origine: e.target.value })} style={inputStyle} placeholder="BIO24 · BIO24 modifié · NOUVEAU — et pourquoi" /></div>
        <div><label style={lbl} htmlFor="cm-note">Note</label>
          <input id="cm-note" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} style={inputStyle} /></div>
      </div>
      <FieldError>{erreur}</FieldError>
    </div>
    </Modale>
  );
}

function CommentaireFiche({ c, referentiel, onFait, onModifier, onFermer }: { c: Commentaire; referentiel: Referentiel; onFait: () => void; onModifier: () => void; onFermer: () => void }) {
  const { showToast, settings } = useGed();
  const [motif, setMotif] = useState('');
  const [retrait, setRetrait] = useState(false);
  const [erreur, setErreur] = useState('');
  const { run, estOccupe } = useBusy();
  const valider = async () => {
    setErreur('');
    /* La version RELUE (14/10/2026) : le serveur refuse si le texte a changé depuis. */
    const r = await ecrire(`${URL_REF}/commentaires/${encodeURIComponent(c.code)}/valider`, { version: c.version });
    if (!r.ok) { setErreur(r.message || 'Validation refusée.'); return; }
    showToast(`${c.code} v${c.version} validé.`); onFait();
  };
  const retirer = () => run('retirer', async () => {
    setErreur('');
    const r = await ecrire(`${URL_REF}/commentaires/${encodeURIComponent(c.code)}/retirer`, { motif });
    if (!r.ok) { setErreur(r.message || 'Retrait refusé.'); return; }
    showToast(`${c.code} retiré.`); onFait();
  });
  const t = TEINTE_STATUT[c.statut] || TEINTE_STATUT.brouillon;
  /* L'habilitation exigée est celle de CHAQUE examen que le texte vise : un texte T1 + T2 demande les deux. */
  const peutApprouver = (() => {
    const par = referentiel.droits.parAnalyse;
    if (!par || !c.analyses.length) return { ok: referentiel.droits.peutValider, motif: referentiel.droits.motifValidation };
    const refus = c.analyses.map((x) => ({ x, v: par[x] })).find((e) => e.v && !e.v.ok);
    return refus ? { ok: false, motif: c.analyses.length > 1 ? `${refus.x} : ${refus.v?.motif}` : String(refus.v?.motif) } : { ok: true, motif: '' };
  })();
  return (
    <Modale titre={c.code} largeur={680} onClose={onFermer} sansSaisie={!retrait}
      sousTitre={<span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={mono}>v{c.version} · {LIB_BLOC[c.bloc] || c.bloc} · {LIB_MODE[c.mode]}</span>
        {pill({ label: LIB_STATUT[c.statut] || c.statut, ...t })}
      </span>}>
    <div style={{ display: 'grid', gap: 10 }}>
      <div style={{ fontSize: 13.5, lineHeight: 1.55, color: 'var(--text-body)', whiteSpace: 'pre-wrap', padding: '10px 12px', background: 'var(--gray-50)', borderRadius: 'var(--radius-sm)' }}>
        {c.texte || <em style={{ color: 'var(--text-muted)' }}>(texte vide — rien à imprimer, et c’est voulu)</em>}
      </div>
      <div style={{ fontSize: 12.5, display: 'grid', gap: 4 }}>
        <div><strong>S’applique :</strong> {phraseCondition(c.condition)} — analyses {c.analyses.join(', ')}</div>
        {c.origine && <div><strong>Origine :</strong> {c.origine}</div>}
        <div style={{ color: 'var(--text-muted)' }}>
          Rédigé par {c.auteur ? userName(settings, c.auteur) : '—'}{c.auteurLe ? ` le ${fmtDateTime(c.auteurLe)}` : ''}
          {c.validateur ? ` · validé par ${userName(settings, c.validateur)} le ${fmtDateTime(c.valideLe)}` : ''}
          {c.retirePar ? ` · retiré par ${userName(settings, c.retirePar)} le ${fmtDateTime(c.retireLe)} — ${c.retireMotif}` : ''}
        </div>
        {c.note && <div style={{ color: 'var(--text-muted)' }}>{c.note}</div>}
        {c.historique.length > 0 && (
          <details><summary style={{ cursor: 'pointer' }}>Versions précédentes ({c.historique.length})</summary>
            <ul style={{ margin: '6px 0 0', paddingInlineStart: 18 }}>
              {[...c.historique].reverse().map((h) => <li key={h.version}><strong>v{h.version}</strong> ({LIB_STATUT[h.statut] || h.statut}, figée le {fmtDateTime(h.figeLe)}) — <span style={{ whiteSpace: 'pre-wrap' }}>{h.texte || '(vide)'}</span></li>)}
            </ul></details>
        )}
      </div>
      <FieldError>{erreur}</FieldError>
      {c.statut !== 'retire' && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {referentiel.droits.peutRediger && <Bouton taille="sm" onClick={onModifier}><I n="pen" s={12} />Modifier</Bouton>}
          {c.statut === 'brouillon' && (peutApprouver.ok
            ? <ConfirmButton label="Approuver le texte" icon="check" question={`Approuver ${c.code} v${c.version} ? Il passera au statut « validé » et servira sur les comptes rendus dès que le référentiel de l’analyse sera complet.`} confirmer="Approuver" onConfirm={valider} />
            : <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Validation : {peutApprouver.motif}.</span>)}
          {referentiel.droits.peutParametrer && !retrait && <Bouton taille="sm" onClick={() => setRetrait(true)}><I n="archive" s={12} />Retirer</Bouton>}
        </div>
      )}
      {retrait && (
        <div style={{ display: 'grid', gap: 8 }}>
          <label style={lbl} htmlFor="cm-retrait">Motif du retrait (obligatoire)</label>
          <textarea id="cm-retrait" rows={2} value={motif} onChange={(e) => setMotif(e.target.value)} style={texteStyle} />
          <PiedActions taille="sm" annuler={{ onClick: () => setRetrait(false) }}
            principale={{ libelle: 'Retirer de la bibliothèque', libelleChargement: 'Retrait…', chargement: estOccupe('retirer'), disabled: motif.trim().length < 5, onClick: () => void retirer() }} />
        </div>
      )}
    </div>
    </Modale>
  );
}

const grilleOnglet: React.CSSProperties = { display: 'grid', gap: 12, minWidth: 0 };

function OngletCommentaires({ referentiel, onFait }: { referentiel: Referentiel; onFait: () => void }) {
  const { showToast, session } = useGed();
  const [ouvert, setOuvert] = useState<string | null>(null);
  const [edition, setEdition] = useState<'nouveau' | string | null>(null);
  const [filtre, setFiltre] = useState<'tous' | 'brouillon' | 'valide' | 'retire'>('tous');
  const lignes = useMemo(() => referentiel.commentaires.filter((c) => filtre === 'tous' || c.statut === filtre), [referentiel, filtre]);
  const courant = ouvert ? referentiel.commentaires.find((c) => c.code === ouvert) || null : null;
  const compte = (s: string) => referentiel.commentaires.filter((c) => c.statut === s).length;
  /* LA VALIDATION GROUPÉE n'offre que ce que le serveur acceptera : brouillon, habilitation de CHAQUE examen
     visé par le texte, et un autre auteur que soi. Le serveur revérifie chaque texte — l'écran ne fait
     qu'annoncer honnêtement ce qui va passer et ce qui restera en brouillon. */
  const brouillons = referentiel.commentaires.filter((c) => c.statut === 'brouillon');
  const validables = brouillons.filter((c) => {
    const par = referentiel.droits.parAnalyse;
    const droit = par && c.analyses.length ? c.analyses.every((x) => par[x]?.ok) : referentiel.droits.peutValider;
    return droit && c.auteur !== session.key;
  });
  const restent = brouillons.length - validables.length;
  const validerLot = async () => {
    /* Chaque texte avec la version annoncée (14/10/2026) : un texte modifié depuis revient « refusé ». */
    const r = await ecrire(`${URL_REF}/commentaires/valider-lot`, { codes: validables.map((c) => c.code), versions: Object.fromEntries(validables.map((c) => [c.code, c.version])) });
    if (!r.ok) { showToast(r.message || 'La validation groupée a échoué.'); return; }
    const faits = (r.corps?.validés as string[] | undefined)?.length ?? 0;
    const refus = (r.corps?.refusés as { code: string; motif: string }[] | undefined) || [];
    showToast(`${faits} texte(s) validé(s)${refus.length ? ` · ${refus.length} refusé(s) : ${refus.map((x) => x.code).join(', ')}` : ''}.`);
    onFait();
  };
  return (
    <div style={grilleOnglet}>
      {!referentiel.droits.peutValider && (
        <div style={encart}>Vous pouvez {referentiel.droits.peutRediger ? 'rédiger mais pas' : 'lire mais ni rédiger ni'} valider : {referentiel.droits.motifValidation}. Valider un texte de compte rendu demande la fonction biologiste <strong>et</strong> l’habilitation {referentiel.parametrage.habilitationValidation || '—'} (Personnel → Habilitations).</div>
      )}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <Segment ariaLabel="Statut" valeur={filtre} onChange={setFiltre} options={[
          { valeur: 'tous', libelle: 'Tous', compteur: referentiel.commentaires.length },
          { valeur: 'brouillon', libelle: 'Brouillons', compteur: compte('brouillon') },
          { valeur: 'valide', libelle: 'Validés', compteur: compte('valide') },
          { valeur: 'retire', libelle: 'Retirés', compteur: compte('retire') },
        ]} />
        <span style={{ flex: 1 }} />
        {validables.length > 0 && (
          <ConfirmButton label={`Approuver ${validables.length} brouillon${validables.length > 1 ? 's' : ''}`} icon="check"
            question={`Approuver les ${validables.length} texte${validables.length > 1 ? 's' : ''} en brouillon que vous pouvez valider ? Chacun passera au statut « validé » sous votre nom et sera tracé au journal.${restent ? ` ${restent} autre${restent > 1 ? 's' : ''} brouillon${restent > 1 ? 's' : ''} reste${restent > 1 ? 'nt' : ''} en attente (vous en êtes l’auteur, ou l’habilitation d’un examen visé vous manque).` : ''}`}
            confirmer="Tout approuver" onConfirm={validerLot} />
        )}
        {referentiel.droits.peutRediger && edition === null && <BoutonCreer onClick={() => { setOuvert(null); setEdition('nouveau'); }}>Nouveau commentaire</BoutonCreer>}
      </div>
      {edition === 'nouveau' && <CommentaireForm c={null} referentiel={referentiel} onFait={() => { setEdition(null); onFait(); }} onAnnule={() => setEdition(null)} />}
      {edition && edition !== 'nouveau' && courant && <CommentaireForm key={courant.code} c={courant} referentiel={referentiel} onFait={() => { setEdition(null); onFait(); }} onAnnule={() => setEdition(null)} />}
      {!edition && courant && <CommentaireFiche key={courant.code} c={courant} referentiel={referentiel} onFait={onFait} onModifier={() => setEdition(courant.code)} onFermer={() => setOuvert(null)} />}
      {/* `overflow: hidden` sur l'enveloppe : c'est ce qui ramène le `min-width: auto`
          de l'item de grille à zéro — sans lui, le tableau large élargit la page. */}
      <div style={{ minWidth: 0, overflow: 'hidden' }}><Tableau
        id="moment-commentaires"
        ajusterLargeur
        niveau="standard"
        defilementHorizontal
        chargement={false}
        vide={{ titre: referentiel.commentaires.length === 0 ? 'Aucun commentaire type : le seed n’a pas été joué.' : 'Aucun commentaire dans ce statut.' }}
        lignes={lignes}
        cleLigne={(c) => c.code}
        ligneProps={(c) => ligneOuvrable(() => { setEdition(null); setOuvert(c.code); }, `Ouvrir ${c.code}`)}
        colonnes={[
          { cle: 'code', libelle: 'Code', valeur: (c) => c.code, epinglee: true, obligatoire: true, rendu: (c) => <span style={{ ...monoCellule, fontWeight: 600, color: 'var(--text-strong)' }}>{c.code}</span> },
          { cle: 'bloc', libelle: 'Bloc', type: 'liste', valeur: (c) => c.bloc, libelleValeur: (v) => LIB_BLOC[v] || v, rendu: (c) => LIB_BLOC[c.bloc] || c.bloc },
          { cle: 'texte', libelle: 'Texte', valeur: (c) => c.texte, rendu: (c) => <span style={{ display: 'block', maxWidth: 520, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.texte || <em style={{ color: 'var(--text-muted)' }}>(vide)</em>}</span> },
          { cle: 'condition', libelle: 'S’applique', valeur: (c) => phraseCondition(c.condition), rendu: (c) => <span style={{ color: 'var(--text-muted)' }}>{phraseCondition(c.condition)}</span> },
          { cle: 'analyses', libelle: 'Analyses', valeur: (c) => c.analyses.join(' '), rendu: (c) => c.analyses.map((a) => a.replace('TRISOMIE_', '')).join(', ') },
          { cle: 'mode', libelle: 'Mode', type: 'liste', valeur: (c) => c.mode, libelleValeur: (v) => LIB_MODE[v] || v, rendu: (c) => LIB_MODE[c.mode] },
          { cle: 'version', libelle: 'Version', type: 'nombre', valeur: (c) => c.version, rendu: (c) => <span style={monoCellule}>v{c.version}</span> },
          { cle: 'statut', libelle: 'Statut', type: 'liste', valeur: (c) => c.statut, libelleValeur: (v) => LIB_STATUT[v] || v, rendu: (c) => { const t = TEINTE_STATUT[c.statut] || TEINTE_STATUT.brouillon; return <Pastille bg={t.bg} fg={t.fg} compacte>{LIB_STATUT[c.statut] || c.statut}</Pastille>; } },
        ]}
      /></div>
    </div>
  );
}

/* ───────────────────────────── Onglet Règles ───────────────────────────── */

function RegleForm({ r, referentiel, onFait, onAnnule }: { r: Regle; referentiel: Referentiel; onFait: () => void; onAnnule: () => void }) {
  const { showToast } = useGed();
  const seuils = referentiel.seuilsRegles?.seuils[r.code] || [];
  const renvoi = referentiel.seuilsRegles?.renvois[r.code] || '';
  const lu = (sl: SeuilRegle) => { const v = r.parametres?.[sl.portee]?.[sl.cle]; return typeof v === 'number' ? v : sl.defaut; };
  const [valeurs, setValeurs] = useState<Record<string, string>>(() => Object.fromEntries(seuils.map((sl) => [sl.cle, String(lu(sl))])));
  const [f, setF] = useState({ niveau: r.niveau, actif: r.actif, message: r.message, commentaireNr: r.commentaireNr || '', note: r.note || '' });
  /* La règle LUE à l'ouverture (14/10/2026) : seuls les champs changés s'écrivent. */
  const [base] = useState(() => ({ niveau: r.niveau, actif: r.actif, message: r.message, commentaireNr: r.commentaireNr || null, note: r.note || '', parametres: r.parametres }));
  const [erreur, setErreur] = useState('');
  const { run, estOccupe } = useBusy();
  const nrs = referentiel.commentaires.filter((c) => c.bloc === 'NR' && c.statut !== 'retire');
  const enregistrer = () => run('save', async () => {
    setErreur('');
    /* Les seuils s'écrivent dans `parametres`, au bloc où le moteur les lit ; le reste des paramètres est conservé tel quel. */
    let parametres: Record<string, Record<string, unknown>> | undefined;
    for (const sl of seuils) {
      const n = Number(valeurs[sl.cle]?.replace(',', '.'));
      if (valeurs[sl.cle]?.trim() === '' || !Number.isInteger(n) || n < sl.min || n > sl.max) { setErreur(`${sl.libelle} : un nombre entier de ${sl.min} à ${sl.max} ${sl.unite}.`); return; }
      if (n === lu(sl)) continue;
      const courant = parametres || r.parametres || {};
      parametres = { ...courant, [sl.portee]: { ...courant[sl.portee], [sl.cle]: n } };
    }
    const x = await ecrire(`${URL_REF}/regles/${encodeURIComponent(r.code)}`, { ...f, ...(parametres ? { parametres } : {}), commentaireNr: f.commentaireNr || null, base });
    if (!x.ok) { setErreur(x.message || 'La règle n’a pas pu être enregistrée.'); return; }
    showToast(`Règle ${r.code} enregistrée.`); onFait();
  });
  return (
    <div style={{ ...card, padding: 18, display: 'grid', gap: 12 }}>
      <h2 style={{ margin: 0, fontSize: 15.5, fontWeight: 700, color: 'var(--text-strong)' }}>{r.code} — {r.libelle}</h2>
      {r.verrou && <div style={encart}>Règle <strong>verrouillée</strong> : son niveau ne s’abaisse pas et elle ne se désactive pas. Seuls le message, le commentaire de non-réalisation et la note se règlent.</div>}
      <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px' }}>
        <div><label style={lbl} htmlFor="rg-niveau">Niveau</label>
          <Selecteur id="rg-niveau" value={f.niveau} onChange={(v) => setF({ ...f, niveau: v as 'A' | 'B' })} style={inputStyle} disabled={r.verrou}>
            <option value="B">B — bloquant</option><option value="A">A — avertissement, levé avec justification</option>
          </Selecteur></div>
        <div><label style={lbl} htmlFor="rg-nr">Commentaire de non-réalisation entraîné</label>
          <Selecteur id="rg-nr" value={f.commentaireNr} onChange={(v) => setF({ ...f, commentaireNr: v })} style={inputStyle}>
            <option value="">— aucun —</option>{nrs.map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
          </Selecteur></div>
        <div style={{ gridColumn: '1 / -1' }}><label style={lbl} htmlFor="rg-message">Message affiché — variables {'{valeur}'} et {'{attendu}'}</label>
          <textarea id="rg-message" rows={2} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} style={texteStyle} /></div>
        {seuils.map((sl) => (
          <div key={sl.cle}><label style={lbl} htmlFor={`rg-seuil-${sl.cle}`}>{sl.libelle} ({sl.unite})</label>
            <input id={`rg-seuil-${sl.cle}`} type="number" step="1" min={sl.min} max={sl.max} value={valeurs[sl.cle] ?? ''} onChange={(e) => setValeurs({ ...valeurs, [sl.cle]: e.target.value })} style={inputStyle} />
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{sl.aide} Valeur de départ : {sl.defaut}.</div></div>
        ))}
        {renvoi && <div style={{ gridColumn: '1 / -1', fontSize: 12.5, color: 'var(--text-muted)' }}>{renvoi}</div>}
        <div style={{ gridColumn: '1 / -1' }}><label style={{ display: 'inline-flex', gap: 6, alignItems: 'center', fontSize: 12.5 }}>
          <input type="checkbox" checked={f.actif} disabled={r.verrou} onChange={(e) => setF({ ...f, actif: e.target.checked })} /> Règle active</label></div>
        <div style={{ gridColumn: '1 / -1' }}><label style={lbl} htmlFor="rg-note">Note</label>
          <input id="rg-note" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} style={inputStyle} /></div>
      </div>
      <FieldError>{erreur}</FieldError>
      <PiedActions taille="sm" annuler={{ onClick: onAnnule }}
        principale={{ libelle: 'Enregistrer', libelleChargement: 'Enregistrement…', chargement: estOccupe('save'), disabled: estOccupe() || !f.message.trim(), onClick: () => void enregistrer() }} />
    </div>
  );
}

function OngletRegles({ referentiel, onFait }: { referentiel: Referentiel; onFait: () => void }) {
  const [ouverte, setOuverte] = useState<string | null>(null);
  const courante = ouverte ? referentiel.regles.find((r) => r.code === ouverte) || null : null;
  return (
    <div style={grilleOnglet}>
      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5, maxWidth: '84ch' }}>
        Les contrôles du dossier (identité, validité, datation, cohérence, référentiel, garde-fous du commentaire libre). La logique vit dans le moteur ;
        ici se règlent le niveau, le message et le commentaire de non-réalisation. Les règles 🔒 protègent l’identité, la valeur et la classe du risque : elles ne s’abaissent pas.
      </p>
      {courante && referentiel.droits.peutParametrer && <RegleForm key={courante.code} r={courante} referentiel={referentiel} onFait={() => { setOuverte(null); onFait(); }} onAnnule={() => setOuverte(null)} />}
      <div style={{ minWidth: 0, overflow: 'hidden' }}><Tableau
        id="moment-regles"
        ajusterLargeur
        niveau="standard"
        defilementHorizontal
        chargement={false}
        vide={{ titre: 'Aucune règle : le seed n’a pas été joué.' }}
        lignes={referentiel.regles}
        cleLigne={(r) => r.code}
        ligneProps={(r) => ligneOuvrable(() => setOuverte(r.code), `Ouvrir ${r.code}`)}
        colonnes={[
          { cle: 'code', libelle: 'Code', valeur: (r) => r.code, epinglee: true, obligatoire: true, rendu: (r) => <span style={{ ...monoCellule, fontWeight: 600, color: 'var(--text-strong)' }}>{r.verrou ? '🔒 ' : ''}{r.code}</span> },
          { cle: 'famille', libelle: 'Famille', type: 'liste', valeur: (r) => r.famille, libelleValeur: (v) => LIB_FAMILLE_REGLE[v] || v, rendu: (r) => LIB_FAMILLE_REGLE[r.famille] || r.famille },
          { cle: 'libelle', libelle: 'Règle', valeur: (r) => r.libelle, rendu: (r) => r.libelle },
          { cle: 'niveau', libelle: 'Niveau', type: 'liste', valeur: (r) => r.niveau, libelleValeur: (v) => (v === 'B' ? 'Bloquant' : 'Avertissement'), rendu: (r) => (r.niveau === 'B'
            ? <Pastille bg="var(--red-50)" fg="var(--red-600)" compacte>Bloquant</Pastille>
            : <Pastille bg="var(--amber-50)" fg="var(--amber-700)" compacte>Avertissement</Pastille>) },
          { cle: 'analyses', libelle: 'Analyses', valeur: (r) => r.analyses.join(' '), rendu: (r) => (r.analyses.length ? r.analyses.map((a) => a.replace('TRISOMIE_', '')).join(', ') : 'toutes') },
          { cle: 'nr', libelle: 'Non-réalisation', valeur: (r) => r.commentaireNr || '', rendu: (r) => <span style={monoCellule}>{r.commentaireNr || '—'}</span> },
          { cle: 'actif', libelle: 'Active', type: 'liste', valeur: (r) => (r.actif ? 'oui' : 'non'), rendu: (r) => (r.actif ? 'oui' : 'non') },
        ]}
      /></div>
    </div>
  );
}

/* ───────────────────────────── Onglet Paramétrage ───────────────────────── */

function OngletParametrage({ referentiel, onFait }: { referentiel: Referentiel; onFait: () => void }) {
  const { showToast, settings } = useGed();
  const p = referentiel.parametrage;
  const [f, setF] = useState({
    libellesSsdwLab: p.libellesSsdwLab || [], appelObligatoireAccru: !!p.appelObligatoireAccru,
    momPlausibleMin: String(p.momPlausibleMin ?? 0.15), momPlausibleMax: String(p.momPlausibleMax ?? 6),
    commentaireLibreMax: String(p.commentaireLibreMax ?? 400), habilitationValidation: p.habilitationValidation || '',
  });
  /* Le paramétrage LU à l'ouverture, valeurs brutes de la base (14/10/2026) — pas les valeurs par
     défaut du formulaire : un réglage jamais écrit vaut « null » en base, et le comparer à 0,15
     ferait un faux conflit. Le serveur n'écrit que ce qui a changé. */
  const [base] = useState(() => ({
    libellesSsdwLab: p.libellesSsdwLab ?? null, appelObligatoireAccru: p.appelObligatoireAccru ?? null,
    momPlausibleMin: p.momPlausibleMin ?? null, momPlausibleMax: p.momPlausibleMax ?? null,
    commentaireLibreMax: p.commentaireLibreMax ?? null, habilitationValidation: p.habilitationValidation ?? null,
  }));
  const [erreur, setErreur] = useState('');
  const { run, estOccupe } = useBusy();
  const enregistrer = () => run('save', async () => {
    setErreur('');
    const r = await ecrire(`${URL_REF}/parametrage`, {
      ...f, momPlausibleMin: Number(f.momPlausibleMin.replace(',', '.')), momPlausibleMax: Number(f.momPlausibleMax.replace(',', '.')), commentaireLibreMax: Number(f.commentaireLibreMax),
      base,
    });
    if (!r.ok) { setErreur(r.message || 'Le paramétrage n’a pas pu être enregistré.'); return; }
    showToast('Paramétrage MoMento enregistré.'); onFait();
  });
  const lire = !referentiel.droits.peutParametrer;
  return (
    <div style={{ ...card, padding: 18, display: 'grid', gap: 14 }}>
      {lire && <div style={encart}>Lecture seule : le paramétrage de MoMento relève du RQ.</div>}
      <div>
        <div style={lbl}>Libellés SsdwLab de la ligne « corrigées par … » → facteur MoMento</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>Un libellé inconnu dans un PDF est <strong>bloquant</strong> (COH-04), jamais ignoré : c’est ici qu’on l’ajoute quand SsdwLab change.</div>
        <div style={{ display: 'grid', gap: 6 }}>
          {f.libellesSsdwLab.map((l, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8 }}>
              <input aria-label="Libellé SsdwLab" value={l.libelle} disabled={lire} style={{ ...inputStyle, ...mono }}
                onChange={(e) => setF({ ...f, libellesSsdwLab: f.libellesSsdwLab.map((x, k) => (k === i ? { ...x, libelle: e.target.value } : x)) })} />
              <Selecteur aria-label="Facteur" value={l.facteur} disabled={lire} style={inputStyle}
                onChange={(v) => setF({ ...f, libellesSsdwLab: f.libellesSsdwLab.map((x, k) => (k === i ? { ...x, facteur: v } : x)) })}>
                {referentiel.vocabulaire.facteurs.map((x) => <option key={x} value={x}>{LIB_FACTEUR[x] || x}</option>)}
              </Selecteur>
              {!lire && <Bouton taille="sm" variante="discret" icone="x" aria-label="Retirer ce libellé" onClick={() => setF({ ...f, libellesSsdwLab: f.libellesSsdwLab.filter((_, k) => k !== i) })} />}
            </div>
          ))}
          {!lire && <div><Bouton taille="sm" onClick={() => setF({ ...f, libellesSsdwLab: [...f.libellesSsdwLab, { libelle: '', facteur: referentiel.vocabulaire.facteurs[0] || 'tabac' }] })}><I n="plus" s={12} />Ajouter un libellé</Bouton></div>}
        </div>
      </div>
      <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px' }}>
        <div style={{ gridColumn: '1 / -1' }}><label style={{ display: 'inline-flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
          <input type="checkbox" checked={f.appelObligatoireAccru} disabled={lire} onChange={(e) => setF({ ...f, appelObligatoireAccru: e.target.checked })} />
          <strong>Appel au prescripteur obligatoire pour tout risque accru</strong></label>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginInlineStart: 22 }}>Décision du 10/10/2026 : oui pendant la double saisie (lot L7) et jusqu’à décision contraire. Un CR accru ne se valide alors pas sans le bloc « Prescripteur prévenu » (sauf injoignable motivé).</div></div>
        <div><label style={lbl} htmlFor="pm-min">MoM plausible — minimum (COH-05)</label>
          <input id="pm-min" type="number" step="0.01" value={f.momPlausibleMin} disabled={lire} onChange={(e) => setF({ ...f, momPlausibleMin: e.target.value })} style={inputStyle} /></div>
        <div><label style={lbl} htmlFor="pm-max">MoM plausible — maximum</label>
          <input id="pm-max" type="number" step="0.1" value={f.momPlausibleMax} disabled={lire} onChange={(e) => setF({ ...f, momPlausibleMax: e.target.value })} style={inputStyle} /></div>
        <div><label style={lbl} htmlFor="pm-long">Longueur maximale du commentaire libre (GF-05)</label>
          <input id="pm-long" type="number" value={f.commentaireLibreMax} disabled={lire} onChange={(e) => setF({ ...f, commentaireLibreMax: e.target.value })} style={inputStyle} /></div>
        <div><label style={lbl} htmlFor="pm-hab">Habilitation exigée pour valider</label>
          <input id="pm-hab" value={f.habilitationValidation} disabled={lire} onChange={(e) => setF({ ...f, habilitationValidation: e.target.value.toUpperCase() })} style={{ ...inputStyle, ...mono }} />
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>Avec la fonction biologiste, toujours. Vide = la fonction seule suffit, et l’écran le dira.</div></div>
      </div>
      {p.modifieLe && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Dernière modification : {p.modifiePar ? userName(settings, p.modifiePar) : '—'}, {fmtDateTime(p.modifieLe)}.</div>}
      <FieldError>{erreur}</FieldError>
      {!lire && <PiedActions taille="sm" principale={{ libelle: 'Enregistrer', libelleChargement: 'Enregistrement…', chargement: estOccupe('save'), disabled: estOccupe(), onClick: () => void enregistrer() }} />}
    </div>
  );
}

/* ───────────────────────────── Aperçu ──────────────────────────────────── */

function OngletApercu({ referentiel }: { referentiel: Referentiel }) {
  const [f, setF] = useState({
    analyse: referentiel.analyses[0]?.ficheCode || 'TRISOMIE_T1', classeT21: 'faible', classeT18: 'faible', tubeNeural: 'faible', nr: '',
    mom: { PAPP_A: '', FBHCG: '', HCG: '', AFP: '' } as Record<string, string>, signaux: [] as string[], brouillonsCompris: true,
  });
  const [res, setRes] = useState<Apercu | null>(null);
  const [erreur, setErreur] = useState('');
  const { run, estOccupe } = useBusy();
  const analyse = referentiel.analyses.find((a) => a.ficheCode === f.analyse);
  const nrs = referentiel.commentaires.filter((c) => c.bloc === 'NR' && c.analyses.includes(f.analyse)).map((c) => c.code);
  const essayer = () => run('apercu', async () => {
    setErreur('');
    const r = await ecrire(`${URL_REF}/apercu`, {
      ...f, nr: f.nr || null, tubeNeural: analyse?.trimestre === 'T2' ? f.tubeNeural : null,
      mom: Object.fromEntries(Object.entries(f.mom).filter(([, v]) => v.trim() !== '').map(([k, v]) => [k, Number(v.replace(',', '.'))])),
    });
    if (!r.ok) { setErreur(r.message || 'L’aperçu n’a pas pu être calculé.'); setRes(null); return; }
    setRes(r.corps as unknown as Apercu);
  });
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5, maxWidth: '84ch' }}>
        La conclusion assemblée sur un dossier <strong>fictif</strong> : libellé → phrase résultat → tube neural → recommandation → commentaires → mentions. Brouillons compris, pour relire avant de valider ; sur un vrai CR, seul le validé sert.
      </p>
      <div style={{ ...card, padding: 16, display: 'grid', gap: 10 }}>
        <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px' }}>
          <div><label style={lbl} htmlFor="ap-an">Analyse</label>
            <Selecteur id="ap-an" value={f.analyse} onChange={(v) => setF({ ...f, analyse: v, nr: '' })} style={inputStyle}>
              {referentiel.analyses.map((a) => <option key={a.ficheCode} value={a.ficheCode}>{a.ficheCode} — {a.trimestre}</option>)}
            </Selecteur></div>
          <div><label style={lbl} htmlFor="ap-nr">Non-réalisation</label>
            <Selecteur id="ap-nr" value={f.nr} onChange={(v) => setF({ ...f, nr: v })} style={inputStyle}>
              <option value="">— calcul réalisé —</option>{nrs.map((c) => <option key={c} value={c}>{c}</option>)}
            </Selecteur></div>
          <div><label style={lbl} htmlFor="ap-t21">Classe T21</label>
            <Selecteur id="ap-t21" value={f.classeT21} onChange={(v) => setF({ ...f, classeT21: v })} style={inputStyle} disabled={!!f.nr}>
              {CLASSES.map((c) => <option key={c} value={c}>{LIB_CLASSE[c]}</option>)}
            </Selecteur></div>
          <div><label style={lbl} htmlFor="ap-t18">Classe T13/18</label>
            <Selecteur id="ap-t18" value={f.classeT18} onChange={(v) => setF({ ...f, classeT18: v })} style={inputStyle} disabled={!!f.nr}>
              {CLASSES.map((c) => <option key={c} value={c}>{LIB_CLASSE[c]}</option>)}
            </Selecteur></div>
          {analyse?.trimestre === 'T2' && <div><label style={lbl} htmlFor="ap-tn">Tube neural (SsdwLab)</label>
            <Selecteur id="ap-tn" value={f.tubeNeural} onChange={(v) => setF({ ...f, tubeNeural: v })} style={inputStyle} disabled={!!f.nr}>
              {TUBES.map((c) => <option key={c} value={c}>{c}</option>)}
            </Selecteur></div>}
          <div><div style={lbl}>MoM</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {(analyse?.trimestre === 'T1' ? ['FBHCG', 'PAPP_A'] : ['HCG', 'AFP']).map((k) => (
                <label key={k} style={{ fontSize: 12, display: 'grid', gap: 2 }}>{k}
                  <input type="number" step="0.01" value={f.mom[k] || ''} onChange={(e) => setF({ ...f, mom: { ...f.mom, [k]: e.target.value } })} style={{ ...inputStyle, width: 110 }} /></label>
              ))}
            </div></div>
          <div style={{ gridColumn: '1 / -1' }}>
            <Cases titre="Signaux du dossier" options={referentiel.vocabulaire.signaux} libelle={(v) => LIB_SIGNAL[v] || v} choisis={f.signaux} onChange={(v) => setF({ ...f, signaux: v })} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}><label style={{ display: 'inline-flex', gap: 6, alignItems: 'center', fontSize: 12.5 }}>
            <input type="checkbox" checked={f.brouillonsCompris} onChange={(e) => setF({ ...f, brouillonsCompris: e.target.checked })} /> Brouillons compris (relecture)</label></div>
        </div>
        <FieldError>{erreur}</FieldError>
        <PiedActions taille="sm" principale={{ libelle: 'Assembler la conclusion', libelleChargement: 'Calcul…', chargement: estOccupe('apercu'), disabled: estOccupe(), onClick: () => void essayer() }} />
      </div>
      {res && (
        <div style={{ ...card, padding: 16, display: 'grid', gap: 10 }}>
          {res.defauts.length > 0 && <div style={{ ...encart, background: 'var(--red-50)', borderInlineStartColor: 'var(--red-600)' }}><strong>Défauts :</strong> {res.defauts.join(' · ')}</div>}
          {res.indecidables.length > 0 && <div style={encart}>Conditions indécidables sans donnée : {res.indecidables.join(', ')}.</div>}
          {res.defauts.length === 0 && <div style={encartVert}>Conclusion complète{res.brouillonsCompris ? ' (brouillons compris)' : ''}.</div>}
          <div style={{ display: 'grid', gap: 8 }}>
            {res.blocs.map((b) => (
              <div key={`${b.bloc}-${b.code}`} style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: 10, fontSize: 13, lineHeight: 1.5 }}>
                <span style={{ ...mono, color: 'var(--text-muted)' }}>{LIB_BLOC[b.bloc] || b.bloc}<br />{b.code} v{b.version}</span>
                <span style={{ whiteSpace: 'pre-wrap' }}>{b.texte || <em style={{ color: 'var(--text-muted)' }}>(rien à imprimer)</em>}</span>
              </div>
            ))}
          </div>
          {res.proposes.length > 0 && <div style={{ fontSize: 12.5 }}><strong>Proposés au biologiste :</strong> {res.proposes.map((p) => p.code).join(', ')}</div>}
          {res.manuels.length > 0 && <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Bibliothèque manuelle et mentions du gabarit : {res.manuels.map((p) => p.code).join(', ')}</div>}
        </div>
      )}
    </div>
  );
}

/* ───────────────────────────── Modèles de CR ───────────────────────────── */

type Modele = {
  code: string; groupe: string; analyse: string; trimestre: 'T1' | 'T2'; titre: string; montre: string; pdf: string; variante: string | null;
  terme: string; datation: string; foetus: string; nr: string | null; t21: string | null; t18: string | null;
  risqueT21: string; risqueT18: string; commentaires: string[]; defauts: string[];
};
type LigneCouverture = { axe: string; situation: string; trimestre: 'T1' | 'T2'; preuve: 'pdf' | 'variante' | 'aucune'; modeles: string[] };
type Modeles = { gabarit: string; groupes: { code: string; libelle: string }[]; modeles: Modele[]; couverture: LigneCouverture[] };

const PREUVE: Record<LigneCouverture['preuve'], { libelle: string; ton: 'success' | 'warning' | 'danger' }> = {
  pdf: { libelle: 'PDF réel', ton: 'success' }, variante: { libelle: 'Variante seulement', ton: 'warning' }, aucune: { libelle: 'Aucun modèle', ton: 'danger' },
};
const TON_CLASSE: Record<string, 'success' | 'warning' | 'danger'> = { faible: 'success', intermédiaire: 'warning', accru: 'danger' };

/** Ce que le modèle conclut, en une pastille : la non-réalisation, sinon la classe la plus haute. */
function PastilleIssue({ m }: { m: Modele }) {
  if (m.nr) return <Pastille ton="muted" compacte title="Non réalisé">{m.nr}</Pastille>;
  const ordre = ['faible', 'intermédiaire', 'accru'];
  const haute = [m.t21, m.t18].filter(Boolean).sort((a, b) => ordre.indexOf(b!) - ordre.indexOf(a!))[0] || 'faible';
  return <Pastille ton={TON_CLASSE[haute]} compacte title={`T21 ${m.risqueT21} · T13/18 ${m.risqueT18}`}>{haute}</Pastille>;
}

function OngletModeles() {
  const [d, setD] = useState<Modeles | null>(null);
  const [erreur, setErreur] = useState('');
  const [vue, setVue] = useState<'galerie' | 'couverture'>('galerie');
  const [trimestre, setTrimestre] = useState<'tous' | 'T1' | 'T2'>('tous');
  const [choisi, setChoisi] = useState<string | null>(null);
  const [html, setHtml] = useState<string | null>(null);

  useEffect(() => {
    let vivant = true;
    void fetch(`${URL_REF}/modeles`, { credentials: 'include' })
      .then(async (r) => { const j = await r.json().catch(() => null); if (!vivant) return; if (!r.ok) { setErreur(j?.error?.message || 'Les modèles n’ont pas pu être fabriqués.'); return; } setD(j); setChoisi((c) => c || j.modeles[0]?.code || null); })
      .catch(() => { if (vivant) setErreur('Les modèles n’ont pas pu être fabriqués.'); });
    return () => { vivant = false; };
  }, []);
  useEffect(() => {
    if (!choisi) return;
    let vivant = true;
    setHtml(null);
    void fetch(`${URL_REF}/modeles/${encodeURIComponent(choisi)}`, { credentials: 'include' })
      .then(async (r) => { const t = await r.text(); if (vivant) setHtml(r.ok ? t : ''); })
      .catch(() => { if (vivant) setHtml(''); });
    return () => { vivant = false; };
  }, [choisi]);

  if (erreur) return <EmptyState titre="Les modèles de compte rendu n’ont pas pu être fabriqués." indice={erreur} />;
  if (!d) return <Skeleton rows={6} />;
  const courant = d.modeles.find((m) => m.code === choisi) || null;
  const visibles = d.modeles.filter((m) => trimestre === 'tous' || m.trimestre === trimestre);
  const compte = (p: LigneCouverture['preuve']) => d.couverture.filter((l) => l.preuve === p).length;
  const ouvrir = (code: string) => { setChoisi(code); setVue('galerie'); setTrimestre('tous'); };

  return (
    <div style={grilleOnglet}>
      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5, maxWidth: '90ch' }}>
        Un compte rendu par situation, <strong>fabriqué à l’instant</strong> depuis un PDF SsdwLab réel anonymisé, par le même chemin qu’un dossier :
        lecture du PDF, contrôles, conclusion avec les textes <strong>validés</strong>, gabarit {d.gabarit}. Une <em>variante</em> part d’un PDF réel et en modifie
        une donnée pour montrer une situation qu’aucun PDF du corpus ne montre ; ses risques restent ceux du PDF d’origine. Un modèle n’est jamais signé.
      </p>
      <Segment ariaLabel="Vue" valeur={vue} onChange={setVue} options={[
        { valeur: 'galerie', libelle: 'Modèles', compteur: d.modeles.length },
        { valeur: 'couverture', libelle: 'Couverture des PDF SsdwLab', compteur: compte('aucune') + compte('variante') },
      ]} />
      {vue === 'galerie' && (
        <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 340px) minmax(0, 1fr)', gap: 16, alignItems: 'start' }}>
          <div style={{ display: 'grid', gap: 12 }}>
            <Segment ariaLabel="Trimestre" valeur={trimestre} onChange={setTrimestre} options={[
              { valeur: 'tous', libelle: 'Tous' }, { valeur: 'T1', libelle: '1er trimestre' }, { valeur: 'T2', libelle: '2e trimestre' },
            ]} />
            {d.groupes.map((g) => {
              const ms = visibles.filter((m) => m.groupe === g.code);
              if (!ms.length) return null;
              return (
                <div key={g.code} style={{ display: 'grid', gap: 4 }}>
                  <div style={{ ...lbl, marginBottom: 2 }}>{g.libelle}</div>
                  {ms.map((m) => (
                    <button key={m.code} type="button" onClick={() => setChoisi(m.code)} aria-pressed={m.code === choisi}
                      style={{ textAlign: 'start', display: 'grid', gap: 4, padding: '8px 10px', borderRadius: 'var(--radius-sm)', cursor: 'pointer', font: 'inherit',
                        border: `1px solid ${m.code === choisi ? 'var(--border-strong)' : 'var(--border-subtle)'}`, background: m.code === choisi ? 'var(--surface-sunken)' : 'var(--surface-card)' }}>
                      <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-strong)', lineHeight: 1.35 }}>{m.titre}</span>
                      <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        <Pastille ton="info" compacte>{m.trimestre}</Pastille>
                        <PastilleIssue m={m} />
                        {m.variante ? <Pastille ton="warning" compacte title={m.variante}>variante</Pastille> : <Pastille ton="neutral" compacte>PDF n° {m.pdf}</Pastille>}
                        {m.defauts.length > 0 && <Pastille ton="danger" compacte title={m.defauts.join(' · ')}>défaut</Pastille>}
                      </span>
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
          <div style={{ display: 'grid', gap: 10, minWidth: 0 }}>
            {courant && (
              <div style={{ ...card, padding: 14, display: 'grid', gap: 6, fontSize: 12.5, lineHeight: 1.5 }}>
                <strong style={{ fontSize: 14, color: 'var(--text-strong)' }}>{courant.titre}</strong>
                <span>{courant.montre}</span>
                <span style={{ color: 'var(--text-muted)' }}>
                  Source : PDF SsdwLab anonymisé n° {courant.pdf}{courant.variante ? ` — variante : ${courant.variante}` : ''}. Terme retenu {courant.terme} · datation : {courant.datation} · {courant.foetus}.
                </span>
                <span style={{ color: 'var(--text-muted)' }}>Textes de la conclusion : <span style={mono}>{courant.commentaires.join(', ')}</span></span>
                {courant.defauts.length > 0 && <div style={{ ...encart, background: 'var(--red-50)', borderInlineStartColor: 'var(--red-600)' }}><strong>Défauts d’assemblage :</strong> {courant.defauts.join(' · ')}</div>}
              </div>
            )}
            {html === null
              ? <Skeleton rows={8} />
              : html === ''
                ? <EmptyState titre="Ce modèle n’a pas pu être rendu." />
                : <iframe title={`Modèle de compte rendu ${courant?.titre || ''}`} srcDoc={html} style={{ width: '100%', height: 1120, border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)', background: 'var(--surface-card)' }} />}
          </div>
        </div>
      )}
      {vue === 'couverture' && (
        <div style={{ display: 'grid', gap: 12 }}>
          <div style={encart}>
            <strong>{compte('pdf')} situations</strong> sont montrées par un PDF SsdwLab réel, <strong>{compte('variante')}</strong> par une variante seulement,
            {' '}<strong>{compte('aucune')}</strong> par aucun modèle. Une situation sans PDF réel n’est pas forcément mal traitée : le moteur la connaît, mais
            aucun PDF reçu ne l’a encore éprouvée. Pour la couvrir, versez un PDF anonymisé de cette situation au corpus.
          </div>
          <div style={{ minWidth: 0, overflow: 'hidden' }}><Tableau
            id="moment-couverture"
            ajusterLargeur
            niveau="standard"
            defilementHorizontal
            chargement={false}
            vide={{ titre: 'Aucune situation énumérée.' }}
            lignes={d.couverture}
            cleLigne={(l) => `${l.trimestre}-${l.axe}-${l.situation}`}
            colonnes={[
              { cle: 'trimestre', libelle: 'Trimestre', type: 'liste', valeur: (l) => l.trimestre, rendu: (l) => l.trimestre },
              { cle: 'axe', libelle: 'Axe', type: 'liste', valeur: (l) => l.axe, rendu: (l) => l.axe },
              { cle: 'situation', libelle: 'Situation', valeur: (l) => l.situation, obligatoire: true, rendu: (l) => l.situation },
              { cle: 'preuve', libelle: 'Montrée par', type: 'liste', valeur: (l) => l.preuve, libelleValeur: (v) => PREUVE[v as LigneCouverture['preuve']]?.libelle || v,
                rendu: (l) => <Pastille ton={PREUVE[l.preuve].ton} compacte>{PREUVE[l.preuve].libelle}</Pastille> },
              { cle: 'modeles', libelle: 'Modèles', valeur: (l) => l.modeles.join(' '), rendu: (l) => (l.modeles.length
                ? <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>{l.modeles.map((c) => <Bouton key={c} variante="discret" taille="sm" onClick={() => ouvrir(c)}>{c}</Bouton>)}</span>
                : '—') },
            ]}
          /></div>
        </div>
      )}
    </div>
  );
}

/* ───────────────────────────── L'écran ─────────────────────────────────── */

type Onglet = 'analyses' | 'commentaires' | 'regles' | 'parametrage' | 'apercu' | 'modeles';

export function Moment() {
  const { booted } = useGed();
  const [d, setD] = useState<Referentiel | null>(null);
  const [chargement, setChargement] = useState(true);
  const [echec, setEchec] = useState(false);
  const [tour, setTour] = useState(0);
  const [onglet, setOnglet] = useState<Onglet>('analyses');

  useEffect(() => {
    let vivant = true;
    void fetch(URL_REF, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (vivant) { setD(j); setEchec(!j); } })
      .catch(() => { if (vivant) { setD(null); setEchec(true); } })
      .finally(() => { if (vivant) setChargement(false); });
    return () => { vivant = false; };
  }, [tour]);
  const recharger = () => { setChargement(true); setTour((t) => t + 1); };

  const entete = <EnteteEcran rubrique="MoMento — dépistage prénatal" titre="Référentiel MoMento"
    sousTitre="Dépistage de la trisomie 21 : ce que le compte rendu lit — paramètres d’analyse, commentaires types, règles de validation." />;

  if (enChargement(booted, chargement, d ? 1 : 0)) {
    return <div style={{ display: 'grid', gap: 16 }}>{entete}<Skeleton rows={4} /></div>;
  }
  if (echec || !d) {
    return (
      <div style={{ display: 'grid', gap: 16 }}>
        {entete}
        <EmptyState titre="Le référentiel n’a pas pu être chargé." indice="Ce n’est pas « rien à voir » : l’écran ne sait rien pour l’instant. La rubrique MoMento vous est peut-être fermée."
          action={<Bouton onClick={recharger}>Réessayer</Bouton>} />
      </div>
    );
  }
  const nBrouillons = d.commentaires.filter((c) => c.statut === 'brouillon').length;
  return (
    <div style={{ display: 'grid', gap: 16, minWidth: 0 }}>
      {entete}
      <Segment ariaLabel="Onglet" valeur={onglet} onChange={setOnglet} options={[
        { valeur: 'analyses', libelle: 'Analyses', compteur: d.analyses.length },
        { valeur: 'commentaires', libelle: 'Commentaires types', compteur: d.commentaires.length },
        { valeur: 'regles', libelle: 'Règles', compteur: d.regles.length },
        { valeur: 'parametrage', libelle: 'Paramétrage' },
        { valeur: 'apercu', libelle: 'Aperçu' },
        { valeur: 'modeles', libelle: 'Modèles de CR' },
      ]} />
      {onglet === 'analyses' && (
        <div style={{ display: 'grid', gap: 12 }}>
          {nBrouillons > 0 && <div style={encart}><strong>{nBrouillons} commentaire{nBrouillons > 1 ? 's' : ''} en brouillon.</strong> Un référentiel ne devient complet que lorsque ses textes sont validés par un biologiste habilité — onglet « Commentaires types ».</div>}
          {d.analyses.length === 0 && <EmptyState titre="Aucune analyse MoMento paramétrée." indice="Le seed pose TRISOMIE_T1 et TRISOMIE_T2 au démarrage du moteur." />}
          {d.analyses.map((a) => <AnalyseCarte key={`${a.ficheCode}-${a.version}`} a={a} peutParametrer={d.droits.peutParametrer} habilitations={d.habilitations || []} parDefaut={d.parametrage.habilitationValidation || null} onFait={recharger} />)}
        </div>
      )}
      {onglet === 'commentaires' && <OngletCommentaires referentiel={d} onFait={recharger} />}
      {onglet === 'regles' && <OngletRegles referentiel={d} onFait={recharger} />}
      {onglet === 'parametrage' && <OngletParametrage key={d.parametrage.modifieLe || 'p'} referentiel={d} onFait={recharger} />}
      {onglet === 'apercu' && <OngletApercu referentiel={d} />}
      {onglet === 'modeles' && <OngletModeles />}
    </div>
  );
}
